/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useMemo } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Helmet from 'react-helmet';
import Cookies from 'js-cookie';

import RedirectRoute from './RedirectRoute';

import { useAlerts, useApp, useAuth } from '../context';
import { COOKIES_OPTIONS, SUPERUSER_LEVEL } from '../../constants';
import { hasSalesAccess, hasSuperuserAccess } from '../../utils';
import { useCurrentDimensions } from '../../utils/hooks';

function RouteConfig({ routes }) {
  const { user } = useAuth();
  const filteredRoutes = useMemo(() => {
    if (!user) return routes;
    if (hasSuperuserAccess(user, [SUPERUSER_LEVEL.FULL_ACCESS])) {
      return routes;
    }
    return routes.filter((route) => !route.path.includes('admin'));
  }, [user]);

  return (
    <Routes>
      {filteredRoutes.map((route) => (
        <Route key={route.path} path={route.path} element={<RouteElement route={route} />} />
      ))}
      <Route path="*" element={<Navigate to="/not_found" />} />
    </Routes>
  );
}

export default RouteConfig;

function TrackedRoute({ route }) {
  const location = useLocation();
  const { user, env, authFrozen } = useAuth();
  const { app, updateApp } = useApp();
  const { setAlert } = useAlerts();
  const { t } = useTranslation();
  const { width: currentWidth } = useCurrentDimensions();
  const isMobile = currentWidth <= 576;

  const routeProps = {
    t,
    user,
    env,
    app,
    updateApp,
    setAlert,
    frozen: authFrozen,
    isMobile,
  };

  useEffect(() => {
    if (!route.disableTracking) {
      Cookies.set('sila_console_referrer', location.pathname, COOKIES_OPTIONS);
    }
  }, [location.pathname, route]);

  useEffect(() => {
    if (app.isLoading) {
      updateApp({ isLoading: false });
    }
  }, [app.isLoading, updateApp]);

  const { seo } = route;
  const title = seo?.title || `${t('seo.title')} - ${route.name}`;
  const description = seo?.description || t('seo.description');

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
      </Helmet>
      <route.layout {...routeProps}>
        <route.component {...routeProps} />
      </route.layout>
    </>
  );
}

function filterRoutes(route, user, authFrozen) {
  const hasSuperUserAccess =
    user &&
    hasSuperuserAccess(user, [
      SUPERUSER_LEVEL.FULL_ACCESS,
      SUPERUSER_LEVEL.ONBOARDING_READ_ONLY,
      SUPERUSER_LEVEL.ONBOARDING_READ_WRITE,
    ]);

  return (
    (user && hasSuperUserAccess && route.admin) ||
    (user && hasSalesAccess(user) && route.sales) ||
    (user && route.restricted) ||
    (!user && !authFrozen && !route.restricted && !route.admin) ||
    (authFrozen && route.frozen) ||
    route.all
  );
}

function RouteElement({ route }) {
  const { user, authFrozen } = useAuth();
  const location = useLocation();
  const from = location.state?.from;

  const shouldRenderTrackedRoute = filterRoutes(route, user, authFrozen);

  if (shouldRenderTrackedRoute) {
    return <TrackedRoute route={route} />;
  }

  return <RedirectRoute from={from} />;
}
